// Organizations, supply targets ("appetite"), fulfilment and the waste-type basket.

import type {
  OrganizationRequest,
  OrganizationType,
  UpdateOrganizationRequest,
} from '../../types/organization.type';
import type { RecyclerAppetitePatchRequest } from '../../types/recycler-appetite.type';
import { materialById, paginate, parseQueryParam } from '../core';
import {
  addWasteType,
  computeFulfillment,
  createOrganization,
  deleteOrganization,
  fail,
  getAppetite,
  isRecyclerOrg,
  listOrganizations,
  listWasteTypes,
  openPreOrderCount,
  patchAppetite,
  removeWasteType,
  replaceWasteTypes,
  requireOrganization,
  updateOrganization,
  updateWasteType,
} from '../data';
import { bodyOf, ok, routes } from './helpers';

/** Why a waste type cannot leave the basket yet (remove_organization_waste_type_). */
function assertRemovable(orgId: string, materialId: string): void {
  const reasons: string[] = [];
  const material = materialById(materialId);
  if (isRecyclerOrg(orgId)) {
    const targetLines = (getAppetite().lines ?? []).filter(
      (line) => line.material_id === materialId || line.material_id === material?.material_type_id,
    ).length;
    if (targetLines) reasons.push(`${targetLines} supply target line(s)`);
  }
  const openOrders = openPreOrderCount(orgId, materialId);
  if (openOrders) reasons.push(`${openOrders} open pre-order(s)`);
  if (reasons.length) {
    fail(
      409,
      `'${material?.name_en ?? materialId}' is still used by ${reasons.join(' and ')}. ` +
        'Remove it from Supply and cancel the open orders first.',
    );
  }
}

export const organizationRoutes = routes([
  [
    'get',
    '/organization',
    (ctx) => {
      const name = parseQueryParam(ctx, 'name')?.toLowerCase();
      const type = parseQueryParam(ctx, 'organization_type') as OrganizationType | undefined;
      const rows = listOrganizations()
        .filter((o) => !name || o.name.toLowerCase().includes(name))
        .filter((o) => !type || o.organization_type === type);
      const page = paginate(rows, parseQueryParam(ctx, 'page') ?? 1, parseQueryParam(ctx, 'limit') ?? 10);
      return ok({ organizations: page.items, total: page.total, page: page.page, limit: page.limit });
    },
  ],
  ['post', '/organization', (ctx) => ok(createOrganization(bodyOf<OrganizationRequest>(ctx)))],
  ['get', '/organization/:orgId', ({ params }) => ok(requireOrganization(params.orgId))],
  [
    'put',
    '/organization/:orgId',
    (ctx) => ok(updateOrganization(ctx.params.orgId, bodyOf<UpdateOrganizationRequest>(ctx))),
  ],
  [
    'delete',
    '/organization/:orgId',
    ({ params }) => {
      deleteOrganization(params.orgId);
      return ok({ deleted: params.orgId });
    },
  ],
  [
    'patch',
    '/organization/:orgId/recycler-appetite',
    (ctx) => ok(patchAppetite(ctx.params.orgId, bodyOf<RecyclerAppetitePatchRequest>(ctx))),
  ],
  [
    'get',
    '/organization/:orgId/recycler-appetite-fulfillment',
    ({ params }) => {
      requireOrganization(params.orgId);
      if (!isRecyclerOrg(params.orgId)) {
        fail(422, 'Recycler appetite fulfillment is only available for recycler organizations');
      }
      return ok(computeFulfillment(params.orgId));
    },
  ],
  [
    'get',
    '/organization/:orgId/waste-types',
    ({ params }) => {
      requireOrganization(params.orgId);
      return ok(listWasteTypes(params.orgId));
    },
  ],
  [
    'post',
    '/organization/:orgId/waste-types',
    (ctx) => {
      requireOrganization(ctx.params.orgId);
      const body = bodyOf<{ material_id: string; client_custom_id: string | null }>(ctx);
      return ok(addWasteType(ctx.params.orgId, String(body.material_id ?? ''), body.client_custom_id));
    },
  ],
  [
    'put',
    '/organization/:orgId/waste-types',
    (ctx) => {
      requireOrganization(ctx.params.orgId);
      const ids = bodyOf<{ material_ids: string[] }>(ctx).material_ids;
      const next = Array.isArray(ids) ? ids.map(String) : [];
      listWasteTypes(ctx.params.orgId)
        .filter((row) => !next.includes(row.material_id))
        .forEach((row) => assertRemovable(ctx.params.orgId, row.material_id));
      return ok(replaceWasteTypes(ctx.params.orgId, next));
    },
  ],
  [
    'patch',
    '/organization/:orgId/waste-types/:materialId',
    (ctx) => {
      requireOrganization(ctx.params.orgId);
      const body = bodyOf<{ client_custom_id: string | null }>(ctx);
      return ok(updateWasteType(ctx.params.orgId, ctx.params.materialId, body.client_custom_id));
    },
  ],
  [
    'delete',
    '/organization/:orgId/waste-types/:materialId',
    ({ params }) => {
      requireOrganization(params.orgId);
      assertRemovable(params.orgId, params.materialId);
      removeWasteType(params.orgId, params.materialId);
      return ok({ removed: params.materialId });
    },
  ],
]);
